import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const Privacy = () => {
  const navigate = useNavigate();

  // Add page-specific meta tags and schema markup for SEO
  usePageMeta({
    title: "Privacy Policy | Let's Be Real",
    description: "Let's Be Real privacy policy. Learn how we collect, use, and protect your data when using our property insights platform.",
    canonical: "https://letsbereal.com.au/privacy",
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "website",
    twitterCard: "summary",
    robots: "index, follow",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy",
      "description": "Privacy policy for Let's Be Real property insights platform",
      "url": "https://letsbereal.com.au/privacy"
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-primary/10 flex-shrink-0">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Privacy Policy</h1>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1">
                Last updated: {new Date().toLocaleDateString("en-AU", { 
                  day: "numeric", 
                  month: "long", 
                  year: "numeric" 
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-6">
              <section>
                <h2 className="text-2xl font-bold mb-3">Overview</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Let's Be Real ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, and safeguard information when 
                  you use our property insights platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Information We Collect</h2>
                <div className="space-y-3 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Addresses You Search</h3>
                    <p className="leading-relaxed">
                      When you search for a property, we temporarily process the address to generate 
                      your report. We cache results for performance but do not permanently store your 
                      personal search history.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Technical Information</h3>
                    <p className="leading-relaxed">
                      We automatically collect standard web analytics data including IP address, 
                      browser type, device information, and timestamps. This helps us improve our 
                      service and prevent abuse.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Cookies and Tracking</h3>
                    <p className="leading-relaxed">
                      We use cookies for essential functionality. Additionally, we use Google AdSense 
                      to display advertisements, which may use cookies to show you relevant ads. You 
                      can control cookie preferences through your browser settings.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Analytics (Firebase Analytics & Google Analytics 4)</h3>
                    <p className="leading-relaxed mb-2">
                      We use <strong>dual tracking</strong> to understand how users interact with our service. 
                      All user activity is sent to both Firebase Analytics and Google Analytics 4 simultaneously:
                    </p>
                    <div className="space-y-3 ml-2">
                      <div>
                        <p className="font-medium text-foreground text-sm mb-1">🔵 Firebase Analytics (G-S270XPKRNV)</p>
                        <p className="text-sm leading-relaxed">
                          Integrated with our Firebase infrastructure for app performance monitoring and 
                          user behavior tracking within the Firebase Console.
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm mb-1">🟢 Google Analytics 4 (G-3BVS8BRPJX)</p>
                        <p className="text-sm leading-relaxed">
                          Our main analytics platform for comprehensive reporting, custom dimensions, and 
                          in-depth analysis of user behavior.
                        </p>
                      </div>
                    </div>
                    <p className="leading-relaxed mt-3 mb-2 font-medium text-foreground">
                      What we track:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                      <li>Page views and navigation patterns</li>
                      <li>Search queries (addresses searched, suburbs, states)</li>
                      <li>Button clicks and user interactions</li>
                      <li>Geographic regions (state/suburb trends)</li>
                      <li>Autocomplete selections and positions</li>
                      <li>Report generation completions</li>
                    </ul>
                    <p className="leading-relaxed mt-3">
                      <strong className="text-foreground">Privacy commitment:</strong> No personally identifiable 
                      information (PII) is collected beyond the property addresses you explicitly search for. 
                      All data is used solely to improve the service and understand usage patterns.
                    </p>
                    <p className="leading-relaxed mt-2">
                      <strong className="text-foreground">Opt-out:</strong> You can opt-out of analytics tracking using 
                      browser extensions (e.g., <a 
                        href="https://tools.google.com/dlpage/gaoptout" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Google Analytics Opt-out Browser Add-on
                      </a>), privacy-focused browsers, or ad blockers. Note that opting out may affect your 
                      ability to use certain features.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">How We Use Your Information</h2>
                <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                  <li>Generate property insights reports</li>
                  <li>Improve our services and user experience</li>
                  <li>Analyze usage patterns and trends via Firebase Analytics and Google Analytics 4</li>
                  <li>Understand which locations and features are most popular</li>
                  <li>Prevent fraud and abuse</li>
                  <li>Comply with legal obligations</li>
                  <li>Display relevant advertisements (via Google AdSense)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Third-Party Services</h2>
                <div className="space-y-3 text-muted-foreground">
                  <p className="leading-relaxed">
                    We use the following third-party services to provide our platform:
                  </p>
                  <ul className="space-y-2 list-disc list-inside ml-4">
                    <li><strong>ChatGPT (OpenAI):</strong> AI-powered insights generation</li>
                    <li><strong>OpenStreetMap:</strong> Geocoding and map data</li>
                    <li><strong>Australian Bureau of Statistics:</strong> Census demographic data</li>
                    <li><strong>Firebase Analytics (Google):</strong> App performance and user behavior tracking</li>
                    <li><strong>Google Analytics 4:</strong> Comprehensive analytics and reporting</li>
                    <li><strong>Google AdSense:</strong> Advertising platform</li>
                    <li><strong>Firebase (Google Cloud):</strong> Hosting and backend infrastructure</li>
                  </ul>
                  <p className="leading-relaxed mt-3">
                    Each service has its own privacy policy. We recommend reviewing their policies 
                    to understand how they handle data.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Google AdSense</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use Google AdSense to display advertisements on our site. Google uses cookies 
                  to serve ads based on your prior visits to our website and other websites. You 
                  may opt out of personalized advertising by visiting{" "}
                  <a 
                    href="https://www.google.com/settings/ads" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Google's Ads Settings
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement reasonable security measures to protect your information. However, 
                  no internet transmission is 100% secure. We cannot guarantee absolute security of 
                  data transmitted to our site.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Your Rights</h2>
                <div className="text-muted-foreground space-y-2">
                  <p className="leading-relaxed">You have the right to:</p>
                  <ul className="space-y-1 list-disc list-inside ml-4">
                    <li>Access the information we hold about you</li>
                    <li>Request correction of inaccurate information</li>
                    <li>Request deletion of your information (subject to legal requirements)</li>
                    <li>Opt out of certain data collection practices</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our service is not intended for children under 13 years of age. We do not 
                  knowingly collect personal information from children under 13.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any 
                  changes by updating the "Last updated" date at the top of this policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Let's Be Real is a satirical property insights tool for entertainment purposes. 
                  Reports should not be used as the sole basis for property investment decisions. 
                  Always conduct thorough research and consult qualified professionals before making 
                  property decisions.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-3">Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about this Privacy Policy or how we handle your data, 
                  please contact us at{" "}
                  <a 
                    href="mailto:help.letsbereal@gmail.com"
                    className="text-primary hover:underline font-medium"
                  >
                    help.letsbereal@gmail.com
                  </a>{" "}
                  or visit our{" "}
                  <Button
                    variant="link"
                    onClick={() => navigate("/about")}
                    className="p-0 h-auto text-primary"
                  >
                    About page
                  </Button>.
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Privacy;

